import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Ban, Crown, LogOut, MailPlus, RefreshCcw, RotateCcw, Send, Trash2, UserPlus, X } from "lucide-react";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { api, errorMessage, unwrap, type S } from "../api/client";
import { ROLE_TONE, useOrg, useOrgId } from "../api/org";
import { Alert, Badge, Button, Card, CopyButton, Field, Modal, PageHead, Select, Spinner, TextInput, useToast } from "../components/ui";
import { useT, type Translate } from "../i18n/I18nProvider";
import { DirIcon } from "../i18n/mirror";
import type { MessageKey } from "../i18n/keys";
import { initials } from "../lib/format";
import { useDates } from "../lib/useDates";
import { NotificationSettings } from "./Notifications";
import { StatusBadge } from "../components/StatusBadge";
import { useOrganization } from "../api/queries";

type Role = S["MembershipRole"];
type Assignable = S["AssignableRole"];
const ASSIGNABLE: Assignable[] = ["admin", "designer", "sales", "viewer"];
const AREA_LABELS: Record<string, MessageKey> = {
  designs_leads: "team.area.designsLeads",
  branding: "team.area.branding",
  widget_keys: "team.area.widgetKeys",
  credits_billing: "team.area.luxBilling",
  team: "team.area.team",
  settings_rules: "team.area.settingsRules",
  integrations: "team.area.integrations",
  audit_log: "team.area.auditLog",
};
const ROLE_DESCRIPTIONS: Record<Role, MessageKey> = {
  owner: "team.role.description.owner",
  admin: "team.role.description.admin",
  designer: "team.role.description.designer",
  sales: "team.role.description.sales",
  viewer: "team.role.description.viewer",
};
const ACTION_NAMES: Record<string, MessageKey> = {
  view: "team.roles.action.view",
  create: "team.roles.action.create",
  edit: "team.roles.action.edit",
  delete: "team.roles.action.delete",
};
const ACTION_LETTERS: Record<string, MessageKey> = {
  view: "team.roles.actionLetter.view",
  create: "team.roles.actionLetter.create",
  edit: "team.roles.actionLetter.edit",
  delete: "team.roles.actionLetter.delete",
};

/** A role's name, the same words as the shell's role chip (shell.role.*). */
export function roleName(t: Translate, role: string): string {
  return ["owner", "admin", "designer", "sales", "viewer"].includes(role) ? t(`shell.role.${role}` as MessageKey) : role;
}

export function useRoles() {
  return useQuery({ queryKey: ["roles"], queryFn: () => unwrap(api.GET("/v1/roles")), staleTime: Infinity });
}

function useTeam(orgId: string) {
  return useQuery({
    queryKey: ["members", orgId],
    queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}/members", { params: { path: { org_id: orgId } } })),
  });
}

function useInvitations(orgId: string) {
  return useQuery({
    queryKey: ["invitations", orgId],
    queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}/invitations", { params: { path: { org_id: orgId } } })),
  });
}

function RolePicker({ value, onChange }: { value: Assignable; onChange: (r: Assignable) => void }) {
  const t = useT();
  return (
    <div className="role-grid" role="radiogroup" aria-label={t("team.invite.role")}>
      {ASSIGNABLE.map((r) => (
        <button key={r} type="button" role="radio" aria-checked={value === r} className={`role-option${value === r ? " active" : ""}`} onClick={() => onChange(r)}>
          <b>{roleName(t, r)}</b>
          <span>{t(ROLE_DESCRIPTIONS[r])}</span>
        </button>
      ))}
    </div>
  );
}

export function InviteModal({ orgId, onClose }: { orgId: string; onClose: () => void }) {
  const t = useT();
  const dates = useDates();
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Assignable>("designer");
  const invite = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/orgs/{org_id}/invitations", { params: { path: { org_id: orgId } }, body: { email: email.trim(), role } })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invitations", orgId] });
      qc.invalidateQueries({ queryKey: ["members", orgId] });
    },
  });
  const sent = invite.data;
  return (
    <Modal
      title={sent ? t("team.invite.sentTitle") : t("team.invite.title")}
      onClose={onClose}
      width={560}
      footer={
        sent ? (
          <Button variant="primary" onClick={onClose}>
            {t("team.common.done")}
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>
              {t("team.common.cancel")}
            </Button>
            <Button variant="primary" icon={<Send />} loading={invite.isPending} disabled={!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())} onClick={() => invite.mutate()}>
              {t("team.invite.send")}
            </Button>
          </>
        )
      }
    >
      {sent ? (
        <div className="stack">
          <Alert tone="ok" title={t("team.invite.emailed", { email: sent.email })}>
            <FormattedMessage
              id="team.invite.sentBody"
              values={{ role: roleName(t, sent.role), date: dates.date(sent.expires_at), b: (c) => <b>{c}</b> }}
            />
          </Alert>
          {sent.accept_url && (
            <Field label={t("team.invite.shareLink")} hint={t("team.invite.shareLinkHint")}>
              <div className="keybox">
                <span className="ellipsis i18n-ltr" style={{ flex: 1 }}>{sent.accept_url}</span>
                <CopyButton text={sent.accept_url} />
              </div>
            </Field>
          )}
        </div>
      ) : (
        <form className="stack" onSubmit={(e) => { e.preventDefault(); invite.mutate(); }}>
          <Field label={t("team.invite.email")} hint={t("team.invite.emailHint")}>
            <TextInput type="email" dir="auto" autoFocus required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("team.invite.emailPlaceholder")} />
          </Field>
          <Field label={t("team.invite.role")}>
            <RolePicker value={role} onChange={setRole} />
          </Field>
          {invite.error && <Alert tone="danger">{errorMessage(invite.error)}</Alert>}
        </form>
      )}
    </Modal>
  );
}

function Seats({ seats }: { seats: S["Seats"] }) {
  const t = useT();
  const intl = useIntl();
  return (
    <div className="seat-row">
      <div className="stat">
        <span className="label">{t("team.seats.active")}</span>
        <div className="value">{intl.formatNumber(seats.active)}</div>
      </div>
      <div className="stat">
        <span className="label">{t("team.seats.invited")}</span>
        <div className="value">{intl.formatNumber(seats.invited)}</div>
      </div>
      <div className="stat">
        <span className="label">{t("team.seats.suspended")}</span>
        <div className="value">{intl.formatNumber(seats.suspended)}</div>
      </div>
    </div>
  );
}

function MemberRow({ m, orgId, canEdit, canRemove, iAmOwner, isMe, onRemove }: {
  m: S["AdminMember"];
  orgId: string;
  canEdit: boolean;
  canRemove: boolean;
  iAmOwner: boolean;
  isMe: boolean;
  onRemove: (m: S["AdminMember"]) => void;
}) {
  const t = useT();
  const dates = useDates();
  const qc = useQueryClient();
  const toast = useToast();
  const update = useMutation({
    mutationFn: (body: S["MemberUpdate"]) =>
      unwrap(api.PATCH("/v1/orgs/{org_id}/members/{user_id}", { params: { path: { org_id: orgId, user_id: m.user_id } }, body })),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["members", orgId] });
      toast(r.status === "suspended" ? t("team.members.toastSuspended", { email: r.email }) : t("team.members.toastRole", { email: r.email, role: roleName(t, r.role) }));
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const isOwner = m.role === "owner";
  const editable = canEdit && !isMe && !isOwner;
  const removable = canRemove && !isMe && (!isOwner || iAmOwner);
  return (
    <tr style={m.status !== "active" ? { opacity: 0.7 } : undefined}>
      <td>
        <div className="row" style={{ flexWrap: "nowrap" }}>
          <div className="org-avatar" style={{ width: 32, height: 32, borderRadius: 99 }}>
            {initials(m.display_name || m.email)}
          </div>
          <div style={{ minWidth: 0 }}>
            <div className="cell-main ellipsis" dir="auto">
              {m.display_name || m.email}
              {isMe && <span className="faint"> {t("team.members.you")}</span>}
            </div>
            {m.display_name && <div className="cell-sub" dir="auto">{m.email}</div>}
          </div>
        </div>
      </td>
      <td style={{ width: 150 }}>
        {editable ? (
          <Select<Role> value={m.role} disabled={update.isPending} onChange={(role) => update.mutate({ role: role as Assignable })} ariaLabel={t("team.members.roleOf", { name: m.display_name || m.email })} options={ASSIGNABLE.map((r) => ({ value: r, label: roleName(t, r) }))} />
        ) : (
          <Badge tone={ROLE_TONE[m.role]} plain>
            {isOwner && <Crown size={11} />} {roleName(t, m.role)}
          </Badge>
        )}
      </td>
      <td>
        <Badge tone={m.status === "active" ? "ok" : m.status === "suspended" ? "danger" : "warn"}>{t("team.members.status", { status: m.status })}</Badge>
      </td>
      <td className="hide-sm muted small">{t("team.members.joinedVia", { source: m.source, date: dates.date(m.created_at) })}</td>
      <td className="hide-sm muted small">{dates.relative(m.last_login_at) ?? t("team.never")}</td>
      <td className="right nowrap">
        {editable &&
          (m.status === "suspended" ? (
            <Button size="sm" variant="ghost" icon={<RotateCcw />} loading={update.isPending} onClick={() => update.mutate({ status: "active" })} title={t("team.members.reactivateHint")}>
              {t("team.members.reactivate")}
            </Button>
          ) : (
            <Button size="sm" variant="ghost" icon={<Ban />} loading={update.isPending} onClick={() => update.mutate({ status: "suspended" })} title={t("team.members.suspendHint")}>
              {t("team.members.suspend")}
            </Button>
          ))}
        {removable && <Button size="sm" variant="ghost" icon={<Trash2 />} onClick={() => onRemove(m)} aria-label={t("team.members.removeLabel", { email: m.email })} />}
      </td>
    </tr>
  );
}

function Invitations({ orgId, canInvite, canRevoke }: { orgId: string; canInvite: boolean; canRevoke: boolean }) {
  const t = useT();
  const dates = useDates();
  const qc = useQueryClient();
  const toast = useToast();
  const invitations = useInvitations(orgId);
  const [link, setLink] = useState<{ email: string; url: string } | null>(null);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["invitations", orgId] });
    qc.invalidateQueries({ queryKey: ["members", orgId] });
  };
  const resend = useMutation({
    mutationFn: (id: string) => unwrap(api.POST("/v1/orgs/{org_id}/invitations/{invitation_id}/resend", { params: { path: { org_id: orgId, invitation_id: id } } })),
    onSuccess: (i) => {
      refresh();
      toast(t("team.invitations.resent", { email: i.email }));
      if (i.accept_url) setLink({ email: i.email, url: i.accept_url });
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE("/v1/orgs/{org_id}/invitations/{invitation_id}", { params: { path: { org_id: orgId, invitation_id: id } } })),
    onSuccess: () => {
      refresh();
      toast(t("team.invitations.revoked"));
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const items = invitations.data?.items ?? [];
  return (
    <Card title={t("team.invitations.title")} description={t("team.invitations.description")} pad={false}>
      {invitations.isLoading ? (
        <Spinner label={t("team.common.loading")} />
      ) : !items.length ? (
        <div className="card-body muted small">{t("team.invitations.empty")}</div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>{t("team.invitations.col.email")}</th>
                <th>{t("team.invitations.col.role")}</th>
                <th>{t("team.invitations.col.status")}</th>
                <th className="hide-sm">{t("team.invitations.col.sent")}</th>
                <th className="hide-sm">{t("team.invitations.col.expires")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.id}>
                  <td>
                    <div className="cell-main" dir="auto">{i.email}</div>
                    {i.invited_by && <div className="cell-sub">{t("team.invitations.by", { email: i.invited_by })}</div>}
                  </td>
                  <td>
                    <Badge plain tone={ROLE_TONE[i.role]}>{roleName(t, i.role)}</Badge>
                  </td>
                  <td>
                    <Badge tone={i.status === "pending" ? "info" : "warn"}>{t("team.invitations.status", { status: i.status })}</Badge>
                  </td>
                  <td className="hide-sm muted small">
                    {i.send_count > 1
                      ? t("team.invitations.sentTimes", { when: dates.relative(i.sent_at) ?? "—", count: i.send_count })
                      : dates.relative(i.sent_at)}
                  </td>
                  <td className="hide-sm muted small">{dates.date(i.expires_at)}</td>
                  <td className="right nowrap">
                    {canInvite && (
                      <Button size="sm" variant="ghost" icon={<RefreshCcw />} loading={resend.isPending && resend.variables === i.id} onClick={() => resend.mutate(i.id)}>
                        {t("team.invitations.resend")}
                      </Button>
                    )}
                    {canRevoke && (
                      <Button size="sm" variant="ghost" icon={<X />} loading={revoke.isPending && revoke.variables === i.id} onClick={() => revoke.mutate(i.id)}>
                        {t("team.invitations.revoke")}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {link && (
        <div className="card-foot">
          <div className="keybox">
            <span className="small muted nowrap" dir="auto">{link.email}</span>
            <span className="ellipsis i18n-ltr" style={{ flex: 1 }}>{link.url}</span>
            <CopyButton text={link.url} />
          </div>
        </div>
      )}
    </Card>
  );
}

function Ownership({ orgId, members, pending }: { orgId: string; members: S["AdminMember"][]; pending: S["OwnershipTransfer"] | null | undefined }) {
  const t = useT();
  const dates = useDates();
  const qc = useQueryClient();
  const toast = useToast();
  const { me } = useOrg();
  const candidates = members.filter((m) => m.status === "active" && m.user_id !== me.user.id && m.role !== "owner");
  const [to, setTo] = useState<string>("");
  const start = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/orgs/{org_id}/ownership-transfers", { params: { path: { org_id: orgId } }, body: { to_user_id: to } })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["members", orgId] });
      toast(t("team.ownership.checkEmail", { email: me.user.email }));
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const cancel = useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE("/v1/orgs/{org_id}/ownership-transfers/{transfer_id}", { params: { path: { org_id: orgId, transfer_id: id } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["members", orgId] }),
  });
  return (
    <Card title={<span className="row" style={{ gap: 8 }}><Crown size={16} /> {t("team.ownership.title")}</span>} description={t("team.ownership.description")}>
      {pending ? (
        <Alert
          tone="warn"
          title={t("team.ownership.waiting", { email: pending.to_email })}
          action={
            <Button size="sm" loading={cancel.isPending} onClick={() => cancel.mutate(pending.id)}>
              {t("team.common.cancel")}
            </Button>
          }
        >
          {t("team.ownership.emailed", { email: me.user.email, date: dates.dateTime(pending.expires_at) })}
        </Alert>
      ) : candidates.length === 0 ? (
        <p className="muted small">{t("team.ownership.noCandidates")}</p>
      ) : (
        <div className="stack">
          <div className="form-grid">
            <Field label={t("team.ownership.newOwner")}>
              <Select value={to} onChange={setTo} ariaLabel={t("team.ownership.newOwner")} options={[{ value: "", label: t("team.ownership.choose") }, ...candidates.map((m) => ({ value: m.user_id, label: t("team.ownership.candidate", { name: m.display_name || m.email, role: roleName(t, m.role) }) }))]} />
            </Field>
          </div>
          <div>
            <Button icon={<MailPlus />} disabled={!to} loading={start.isPending} onClick={() => start.mutate()}>
              {t("team.ownership.send")}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

export function RoleMatrix() {
  const t = useT();
  const intl = useIntl();
  const roles = useRoles();
  if (!roles.data) return <Spinner label={t("team.common.loading")} />;
  const { areas, roles: list } = roles.data;
  const actionName = (a: string) => (ACTION_NAMES[a] ? t(ACTION_NAMES[a]) : a);
  return (
    <div className="table-wrap">
      <table className="matrix">
        <thead>
          <tr>
            <th>{t("team.roles.area")}</th>
            {list.map((r) => (
              <th key={r.role}>{roleName(t, r.role)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {areas.map((area) => (
            <tr key={area}>
              <td>{AREA_LABELS[area] ? t(AREA_LABELS[area]) : area}</td>
              {list.map((r) => {
                const allowed = roles.data.actions.filter((a) => r.permissions.includes(`${area}:${a}`)).map(actionName);
                return (
                  <td key={r.role}>
                    <span className="perm" aria-label={t("team.roles.cellLabel", { role: roleName(t, r.role), actions: allowed.length ? intl.formatList(allowed, { type: "conjunction" }) : t("team.roles.none") })}>
                      {roles.data.actions.map((a) => (
                        <i key={a} className={r.permissions.includes(`${area}:${a}`) ? "on" : undefined} title={actionName(a)}>
                          {ACTION_LETTERS[a] ? t(ACTION_LETTERS[a]) : a[0].toUpperCase()}
                        </i>
                      ))}
                    </span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="small faint" style={{ padding: "10px 10px 0" }}>{t("team.roles.legend")}</p>
    </div>
  );
}

export function Team() {
  const t = useT();
  const orgId = useOrgId();
  const { me, can, role, orgName, adminView } = useOrg();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const team = useTeam(orgId);
  const [inviting, setInviting] = useState(false);
  const [removing, setRemoving] = useState<S["AdminMember"] | null>(null);
  const [leaving, setLeaving] = useState(false);
  const iAmOwner = role === "owner" || adminView;
  const remove = useMutation({
    mutationFn: (m: S["AdminMember"]) => unwrap(api.DELETE("/v1/orgs/{org_id}/members/{user_id}", { params: { path: { org_id: orgId, user_id: m.user_id } } })),
    onSuccess: (_, m) => {
      qc.invalidateQueries({ queryKey: ["members", orgId] });
      toast(t("team.remove.done", { email: m.email, org: orgName }));
      setRemoving(null);
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const leave = useMutation({
    mutationFn: () => unwrap(api.DELETE("/v1/orgs/{org_id}/membership", { params: { path: { org_id: orgId } } })),
    onSuccess: async () => {
      localStorage.removeItem("solaros.org");
      await qc.invalidateQueries({ queryKey: ["me"] });
      navigate("/");
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const data = team.data;
  const company = useOrganization(orgId);
  return (
    <>
      <PageHead
        title={
          <span className="row" style={{ gap: 10 }}>
            {t("team.page.title")} <StatusBadge badge={company.data?.badge} />
          </span>
        }
        description={t("team.page.description")}
        actions={
          can("team", "create") && (
            <Button variant="primary" icon={<UserPlus />} onClick={() => setInviting(true)}>
              {t("team.page.invite")}
            </Button>
          )
        }
      />
      <div className="stack team-stack">
        {team.isLoading ? (
          <Spinner label={t("team.common.loading")} />
        ) : team.error ? (
          <Alert tone="danger">{errorMessage(team.error)}</Alert>
        ) : (
          <>
            <Card pad={false} title={t("team.members.title")} actions={<Seats seats={data!.seats} />}>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t("team.members.col.member")}</th>
                      <th>{t("team.members.col.role")}</th>
                      <th>{t("team.members.col.status")}</th>
                      <th className="hide-sm">{t("team.members.col.joined")}</th>
                      <th className="hide-sm">{t("team.members.col.lastSignIn")}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {data!.items.map((m) => (
                      <MemberRow
                        key={m.user_id}
                        m={m}
                        orgId={orgId}
                        canEdit={can("team", "edit")}
                        canRemove={can("team", "delete")}
                        iAmOwner={iAmOwner}
                        isMe={m.user_id === me.user.id}
                        onRemove={setRemoving}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
            <Invitations orgId={orgId} canInvite={can("team", "create")} canRevoke={can("team", "delete")} />
            {role === "owner" && <Ownership orgId={orgId} members={data!.items} pending={data!.pending_transfer} />}
          </>
        )}
        <Card title={t("team.roles.title")} description={t("team.roles.description")}>
          <RoleMatrix />
        </Card>
        <NotificationSettings />
        {!adminView && (
          <Card title={t("team.leave.title")} description={t("team.leave.description")}>
            <Button variant="danger" icon={<DirIcon><LogOut /></DirIcon>} onClick={() => setLeaving(true)}>
              {t("team.leave.button", { org: orgName })}
            </Button>
          </Card>
        )}
      </div>
      {inviting && <InviteModal orgId={orgId} onClose={() => setInviting(false)} />}
      {removing && (
        <Modal
          title={t("team.remove.title", { name: removing.display_name || removing.email })}
          onClose={() => setRemoving(null)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setRemoving(null)}>
                {t("team.common.cancel")}
              </Button>
              <Button variant="danger" loading={remove.isPending} onClick={() => remove.mutate(removing)}>
                {t("team.common.remove")}
              </Button>
            </>
          }
        >
          <p className="muted">{t("team.remove.body", { org: orgName })}</p>
        </Modal>
      )}
      {leaving && (
        <Modal
          title={t("team.leave.confirmTitle", { org: orgName })}
          onClose={() => setLeaving(false)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setLeaving(false)}>
                {t("team.common.cancel")}
              </Button>
              <Button variant="danger" loading={leave.isPending} onClick={() => leave.mutate()}>
                {t("team.leave.confirm")}
              </Button>
            </>
          }
        >
          <div className="stack">
            <p className="muted">{t("team.leave.body")}</p>
            {role === "owner" && <Alert tone="warn">{t("team.leave.ownerFirst")}</Alert>}
            {leave.error && <Alert tone="danger">{errorMessage(leave.error)}</Alert>}
          </div>
        </Modal>
      )}
    </>
  );
}
